import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { postRegistrarAusencia, deleteAusencia, updateObservacionesAusencia, fetchCeldasHorariasPorFecha } from './Guardias-Api';
import './AbsenceMgntForm.css';

const AbsenceMgntForm = ({ franjasHorarias, onClose, onAbsenceRegistered, profesorId }) => {
    const [fechaAusencia, setFechaAusencia] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [celdasHorarias, setCeldasHorarias] = useState([]);
    // Mantendremos las observaciones por celda horaria en un objeto
    const [observacionesPorCelda, setObservacionesPorCelda] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingCeldas, setLoadingCeldas] = useState(false);
    const [error, setError] = useState(null);

    const cargarCeldasHorarias = async () => {
        if (!fechaAusencia) return;

        setLoadingCeldas(true);
        setError(null);

        try {
            const celdas = await fetchCeldasHorariasPorFecha(profesorId, fechaAusencia);
            setCeldasHorarias(celdas);
            // Inicializar el estado de observaciones para cada celda
            const initialObservaciones = {};
            celdas.forEach(celda => {
                initialObservaciones[celda.id] = celda.ausencia ? celda.ausenciaDTO.observaciones : ''; // O la observación existente si la hubiera
            });
            setObservacionesPorCelda(initialObservaciones);
        } catch (error) {
            setCeldasHorarias([]);
            setError("Error al cargar celdas horarias: " + error.message);
            console.error("Error al cargar celdas horarias:", error);
        } finally {
            setLoadingCeldas(false);
        }
    };

    useEffect(() => {
        cargarCeldasHorarias();
    }, [fechaAusencia, profesorId]);

    const handleObservacionChange = (celdaId, nuevaObservacion) => {
        setObservacionesPorCelda(prevObservaciones => ({
            ...prevObservaciones,
            [celdaId]: nuevaObservacion,
        }));
    };

    const handleToggleAusencia = async (celdaId, franjaId, tieneAusencia, ausenciaId) => {
        setLoading(true);
        setError(null);
        const observacion = observacionesPorCelda[celdaId] || '';

        try {
            if (tieneAusencia) {
                // Eliminar ausencia existente
                await deleteAusencia(ausenciaId);
                //alert("Ausencia eliminada correctamente");
            } else {
                // Registrar nueva ausencia con la observación específica
                await postRegistrarAusencia(
                    profesorId,
                    fechaAusencia,
                    franjaId,
                    observacion
                );
                //alert("Ausencia registrada correctamente");
            }

            cargarCeldasHorarias();

            // Notificar al componente padre para actualizar el calendario
            onAbsenceRegistered();
        } catch (error) {
            setError(`Error al ${tieneAusencia ? 'eliminar' : 'registrar'} ausencia: ${error.message}`);
            console.error(`Error al ${tieneAusencia ? 'eliminar' : 'registrar'} ausencia:`, error);
        } finally {
            setLoading(false);
        }
    };

    const onClickRegistrarDiaCompleto = async () => {
        setLoading(true);
        setError(null);

        try {
            const celdasSinAusencia = celdasHorarias.filter(celda => !celda.ausencia);
            if (celdasSinAusencia.length === 0) {
                setError("Ya hay ausencia registrada en todas las horas de ese día");
                return;
            }
            // Se registra hora a hora, manteniendo las observaciones propias de cada celda
            for (const celda of celdasSinAusencia) {
                const observacion = observacionesPorCelda[celda.id] || '';
                await postRegistrarAusencia(profesorId, fechaAusencia, celda.franjaId, observacion);
            }
            cargarCeldasHorarias();
            onAbsenceRegistered();
        } catch (error) {
            setError(`Error al registrar ausencia de día completo: ${error.message}`);
            console.error("Error al registrar ausencia de día completo:", error);
        } finally {
            setLoading(false);
        }
    }

    const handleActualizarObservacion = async (ausenciaId, observacion) => {
        if (!ausenciaId) return;
        setLoading(true);
        setError(null);
        try {
            await updateObservacionesAusencia(ausenciaId, observacion);
            cargarCeldasHorarias();
            onAbsenceRegistered();
        } catch (error) {
            const msg = error?.response?.data?.error || `Error al actualizar la observación: ${error.message}`;
            setError(msg);
            console.error("Error al actualizar la observación:", error);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{ marginTop: '5rem' }} className="modal fade" id="modalRegistrarAusencia" tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header bg-warning text-white">
                        <h5 className="modal-title">Gestionar Ausencias</h5>
                        <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <form>
                            <div className="row mb-3">
                                <div className="col-md-12">
                                    <label htmlFor="fechaAusencia" className="form-label">Fecha:</label>
                                    <input
                                        type="date"
                                        id="fechaAusencia"
                                        className="form-control"
                                        value={fechaAusencia}
                                        onChange={(e) => setFechaAusencia(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Celdas Horarias:</label>
                                {loadingCeldas ? (
                                    <div className="text-center py-3">
                                        <div className="spinner-border text-warning" role="status">
                                            <span className="visually-hidden">Cargando...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="d-grid gap-2"> {/* Usamos d-grid para apilar los "chips" */}
                                        {celdasHorarias.length > 0 ? (
                                            celdasHorarias.map(celda => (
                                                <div
                                                    key={celda.id}
                                                    className={`p-2 rounded border`}
                                                >
                                                    <div className="chip-header">
                                                        <div className={`hora ${celda.ausencia ? 'text-danger' : ''}`}>
                                                            {`${celda.horaInicio} - ${celda.horaFin}`}
                                                        </div>
                                                        {celda.franja && (
                                                            <span className="franja">({celda.franja})</span>
                                                        )}
                                                    </div>
                                                    <div className="chip-details">
                                                        <div>
                                                            <span className="detail-label">Detalle:</span>
                                                            <span>{celda.detalle}</span>
                                                        </div>
                                                        {celda.grupos && (
                                                            <div>
                                                                <span className="group-label">Grupos:</span>
                                                                <span className="groups">{celda.grupos}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="chip-actions">
                                                        <div className="observations flex-grow-1">
                                                            <label htmlFor={`observaciones-${celda.id}`} className="form-label visually-hidden">Observaciones:</label>
                                                            <input
                                                                type="text"
                                                                id={`observaciones-${celda.id}`}
                                                                className="form-control form-control-sm"
                                                                placeholder="Observaciones"
                                                                value={observacionesPorCelda[celda.id] || ''}
                                                                onChange={(e) => handleObservacionChange(celda.id, e.target.value)}
                                                                disabled={loading}
                                                            />
                                                        </div>
                                                        {celda.ausencia ? (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-success"
                                                                    onClick={() => handleActualizarObservacion(celda.ausenciaDTO?.id, observacionesPorCelda[celda.id] || '')}
                                                                    disabled={loading || celda.ausenciaDTO?.firmada === true}
                                                                >
                                                                    <i className="bi bi-check2 me-1"></i>
                                                                    Actualizar
                                                                </button>
                                                                {celda.ausenciaDTO?.firmada === true && (
                                                                    <span className="badge bg-secondary align-self-center">Firmada</span>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-danger"
                                                                    onClick={() => handleToggleAusencia(celda.id, celda.franjaId, true, celda.ausenciaDTO?.id)}
                                                                    disabled={loading || (fechaAusencia && new Date(fechaAusencia) < new Date().setHours(0, 0, 0, 0))}
                                                                >
                                                                    <i className="bi bi-trash me-1"></i>
                                                                    Eliminar
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-primary"
                                                                onClick={() => handleToggleAusencia(celda.id, celda.franjaId, false, null)}
                                                                disabled={loading || (fechaAusencia && new Date(fechaAusencia) < new Date().setHours(0, 0, 0, 0))}
                                                            >
                                                                <i className="bi bi-plus-circle me-1"></i>
                                                                Registrar
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="alert alert-info">
                                                No hay celdas horarias disponibles para esta fecha
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {error && <div className="alert alert-danger">{error}</div>}

                            <div className="modal-footer">
                                <button 
                                    type="button" className="btn btn-warning"
                                    onClick={onClickRegistrarDiaCompleto}
                                    disabled={loading || (fechaAusencia && new Date(fechaAusencia) < new Date().setHours(0, 0, 0, 0))}                                    
                                >
                                    Día completo
                                </button>                                
                                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal" onClick={onClose}>
                                    Cerrar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AbsenceMgntForm;